import React from 'react';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAccessibility } from '../utils/accessibility';
import { handleAnchorClick } from '../utils/navigation';

interface AccessibleButtonProps {
  children: React.ReactNode;
  ariaLabelKey?: string;
  ariaDescribedBy?: string;
  type?: 'primary' | 'secondary' | 'tertiary';
  size?: 'small' | 'middle' | 'large';
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  href?: string;
  target?: string;
  role?: string;
  isLink? : boolean;
  isUnderlined? : boolean;
}

export const AccessibleButton: React.FC<AccessibleButtonProps> = ({
  children,
  ariaLabelKey,
  ariaDescribedBy,
  isLink = false,
  isUnderlined = false,
  type = 'primary',
  size = 'default',
  onClick,
  className = '',
  disabled = false,
  loading = false,
  href,
  target,
  role,
  ...props
}) => {
  const { getText } = useAccessibility();
  const navigate = useNavigate();
  
  const buttonClass = {
    primary: 'btn-primary',
    secondary: 'btn-secondary', 
    tertiary: 'btn-tertiary'
  }[type];

  const ariaAttributes = {
    'aria-label': ariaLabelKey ? getText(ariaLabelKey) : undefined,
    'aria-describedby': ariaDescribedBy,
    role,
    ...props
  };

  // Si c'est un lien
  if (href && isLink) {
    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        onClick();
      }
      handleAnchorClick(event, href, navigate);
    };

    return (
      <a
        href={href}
        target={target}
        className={`${buttonClass ? buttonClass : ''} ${className ? className : ''} inline-flex items-center justify-center ${isUnderlined ? '' : 'no-underline'}`}
        {...ariaAttributes}
        onClick={handleClick}
      >
        {children}
        {target === '_blank' && (
          <span className="sr-only"> - {getText('screenReader.newWindow')}</span>
        )}
      </a>
    );
  }

  // Bouton standard
  return (
    <Button
      size="small"
      className={`${buttonClass} ${className}`}
      onClick={onClick}
      disabled={disabled}
      loading={loading}
      {...ariaAttributes}
    >
      {children}
    </Button>
  );
};